// ─────────────────────────────────────────────
// useTranslation hook
//
// Wraps the i18n translation function with the current
// locale from the locale store.
//
// Usage:
//   const { t } = useTranslation();
//   t("nav.dashboard") // returns translated string
// ─────────────────────────────────────────────

import { useLocaleStore } from "@/store/locale.store";
import { t as translate, type TranslateParams } from "@/lib/i18n";
import type { Language } from "@/types";

interface UseTranslationResult {
  /** Translate a dot-notation key using the current locale */
  t: (key: string, params?: TranslateParams) => string;
  /** Current locale language code */
  language: Language;
  /** Change the current language */
  setLanguage: (lang: Language) => void;
}

export function useTranslation(): UseTranslationResult {
  const { language, setLanguage } = useLocaleStore();

  const t = (key: string, params?: TranslateParams) => translate(key, language, params);

  return { t, language, setLanguage };
}
